import { useState } from 'react';
import { DownloadIcon, ExpandIcon, LinkedinIcon } from 'lucide-react';
import { Button, Modal, ModalContent } from '@sandtech-hub-monorepo/ui-kit';

type IssuedCertificate = {
  id: string;
  imageUrl?: string | null;
  linkedInCertificateUrl?: string | null;
};

type Props = {
  programName: string;
  issuedCertificate: IssuedCertificate;
};

export const GraduatedCertificateCard = ({
  programName,
  issuedCertificate,
}: Props) => {
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  if (!issuedCertificate.imageUrl) return null;

  return (
    <>
      <div className="flex flex-col sm:flex-row items-start gap-4 p-4 rounded-lg bg-background-tertiary border border-border">
        <button
          type="button"
          onClick={() => setIsPreviewOpen(true)}
          aria-label="View certificate larger"
          className="group relative block w-full sm:w-48 rounded-md overflow-hidden shrink-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        >
          <img
            src={issuedCertificate.imageUrl}
            alt={`Certificate for ${programName}`}
            className="w-full h-auto border border-border rounded-md"
            width={1280}
            height={720}
            loading="lazy"
          />
          <div className="pointer-events-none absolute top-2 right-2 flex items-center gap-1.5 rounded-md bg-background/90 backdrop-blur-sm px-2 py-1 text-xs font-medium text-foreground shadow-sm">
            <ExpandIcon className="w-3 h-3" />
          </div>
        </button>
        <div className="flex flex-col gap-2 flex-1">
          <h3 className="text-base font-semibold text-heading">
            Congratulations on completing {programName}!
          </h3>
          <p className="text-sm text-muted-foreground">
            We're thrilled to celebrate your achievement! Hard work pays off.
            You've earned the skills, knowledge, and confidence to thrive in
            your professional journey. The best is yet to come.
          </p>
          <div className="flex flex-wrap gap-2 mt-1">
            {issuedCertificate.linkedInCertificateUrl && (
              <Button asChild size="sm" className="gap-2 rounded-full">
                <a
                  href={issuedCertificate.linkedInCertificateUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <LinkedinIcon className="w-4 h-4" />
                  Share Certificate
                </a>
              </Button>
            )}
            <Button
              asChild
              variant="outline"
              size="sm"
              className="gap-2 rounded-full"
            >
              <a
                href={issuedCertificate.imageUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                <DownloadIcon className="w-4 h-4" />
                Download Certificate
              </a>
            </Button>
          </div>
        </div>
      </div>

      <Modal open={isPreviewOpen} onOpenChange={setIsPreviewOpen}>
        <ModalContent className="md:w-[min(96vw,1100px)] max-w-none p-0 overflow-hidden border-0 [&>div:nth-child(2)]:overflow-hidden [&>div:nth-child(2)]:m-0 [&>div:nth-child(2)]:p-0">
          <div className="relative bg-background px-6 sm:px-10 pt-10 pb-8">
            <div className="relative mx-auto max-w-[960px]">
              <img
                src={issuedCertificate.imageUrl}
                alt={`Certificate for ${programName}`}
                className="w-full h-auto rounded-lg shadow-lg"
                width={1280}
                height={720}
              />
            </div>
            <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
              {issuedCertificate.linkedInCertificateUrl && (
                <Button asChild className="gap-2">
                  <a
                    href={issuedCertificate.linkedInCertificateUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <LinkedinIcon className="w-4 h-4" />
                    Share Certificate
                  </a>
                </Button>
              )}
              <Button asChild variant="outline" className="gap-2">
                <a
                  href={issuedCertificate.imageUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <DownloadIcon className="w-4 h-4" />
                  Download Certificate
                </a>
              </Button>
            </div>
          </div>
        </ModalContent>
      </Modal>
    </>
  );
};
